import { ffmpegCoreUrl, ffmpegWasmUrl } from '@shared/config/runtime';
import { ASSET_MAX_BYTES } from '@shared/media/uploadPolicy';
import { MediaProcessingError } from './errors';
import type { MediaProcessingUse, MediaQualityIntent } from './types';
import type { VideoInspection, VideoTranscoder } from './videoProcessor';

/** Production browser video transcoder. FFmpeg is loaded lazily and runs in its own worker. */
const FFMPEG_SCRIPT = '/vendor/ffmpeg/ffmpeg.js';
const INPUT_BASENAME = 'locs-video-input';
const OUTPUT_BASENAME = 'locs-video-output';

type FfmpegInstance = {
  load(config?: { coreURL?: string; wasmURL?: string; workerURL?: string }, options?: { signal?: AbortSignal }): Promise<boolean>;
  writeFile(path: string, data: Uint8Array, options?: { signal?: AbortSignal }): Promise<boolean>;
  exec(args: string[], timeout?: number, options?: { signal?: AbortSignal }): Promise<number>;
  readFile(path: string, encoding?: string, options?: { signal?: AbortSignal }): Promise<Uint8Array | string>;
  deleteFile(path: string, options?: { signal?: AbortSignal }): Promise<boolean>;
  terminate(): void;
};

let scriptPromise: Promise<void> | null = null;
let instancePromise: Promise<FfmpegInstance> | null = null;
let queue = Promise.resolve();

function runtimeUrl(path: string) {
  return typeof window === 'undefined' ? path : new URL(path, window.location.href).toString();
}

function abortError() {
  return new MediaProcessingError('cancelled', '视频处理已取消');
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw abortError();
}

async function loadScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new MediaProcessingError('unsupported-input', '当前环境不支持 FFmpeg 视频处理');
  }
  if (window.FFmpegWASM?.FFmpeg) return;
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-locs-ffmpeg-video="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('FFmpeg 脚本加载失败')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = runtimeUrl(FFMPEG_SCRIPT);
      script.async = true;
      script.dataset.locsFfmpegVideo = 'true';
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        script.remove();
        reject(new Error('FFmpeg 脚本加载失败'));
      };
      document.head.appendChild(script);
    });
  }
  await scriptPromise;
  if (!window.FFmpegWASM?.FFmpeg) throw new Error('FFmpeg 脚本未导出 FFmpeg');
}

async function getInstance() {
  if (!instancePromise) {
    instancePromise = (async () => {
      await loadScript();
      const Constructor = window.FFmpegWASM?.FFmpeg as unknown as (new () => FfmpegInstance) | undefined;
      if (!Constructor) throw new Error('FFmpeg 初始化失败');
      const ffmpeg = new Constructor();
      await ffmpeg.load({ coreURL: runtimeUrl(ffmpegCoreUrl), wasmURL: runtimeUrl(ffmpegWasmUrl) });
      return ffmpeg;
    })();
  }
  return instancePromise.catch((error) => {
    resetInstance();
    throw error;
  });
}

function resetInstance() {
  instancePromise = null;
}

function extensionFor(mimeType: string) {
  if (mimeType.includes('quicktime')) return 'mov';
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogv';
  return 'mp4';
}

function outputSettings(
  mimeType: 'video/mp4' | 'video/webm',
  quality: MediaQualityIntent,
  source: VideoInspection,
  attempt: number,
) {
  const profiles = quality === 'hd'
    ? [{ maxDimension: 1920, crf: 25 }, { maxDimension: 1280, crf: 29 }, { maxDimension: 960, crf: 32 }]
    : [{ maxDimension: 1280, crf: 29 }, { maxDimension: 960, crf: 32 }, { maxDimension: 720, crf: 35 }];
  const profile = profiles[Math.min(attempt, profiles.length - 1)]!;
  const maxDimension = profile.maxDimension;
  const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
  const width = Math.max(2, Math.round(source.width * scale / 2) * 2);
  const height = Math.max(2, Math.round(source.height * scale / 2) * 2);
  if (mimeType === 'video/webm') {
    return { width, height, args: ['-c:v', 'libvpx-vp9', '-crf', String(profile.crf + 5), '-b:v', '0', '-c:a', 'libopus', '-b:a', '80k'] };
  }
  return { width, height, args: ['-c:v', 'libx264', '-preset', 'veryfast', '-crf', String(profile.crf), '-pix_fmt', 'yuv420p', '-c:a', 'aac', '-b:a', '80k', '-movflags', '+faststart'] };
}

async function runQueued<T>(job: () => Promise<T>) {
  const next = queue.then(job, job);
  queue = next.then(() => undefined, () => undefined);
  return next;
}

export type FfmpegVideoTranscoderOptions = {
  outputMimeType?: 'video/mp4' | 'video/webm';
  timeoutMs?: number;
  maxOutputBytes?: number;
};

export function createFfmpegVideoTranscoder(options: FfmpegVideoTranscoderOptions = {}): VideoTranscoder {
  const outputMimeType = options.outputMimeType ?? 'video/mp4';
  const timeoutMs = options.timeoutMs ?? 15 * 60 * 1000;
  const maxOutputBytes = options.maxOutputBytes ?? ASSET_MAX_BYTES;
  return ({ file, source, quality, signal }: { file: File; source: VideoInspection; quality: MediaQualityIntent; use: MediaProcessingUse; signal?: AbortSignal }) => runQueued(async () => {
    throwIfAborted(signal);
    const ffmpeg = await getInstance();
    const inputPath = `${INPUT_BASENAME}.${extensionFor(file.type)}`;
    const outputExtension = outputMimeType === 'video/webm' ? 'webm' : 'mp4';
    const outputPath = `${OUTPUT_BASENAME}.${outputExtension}`;
    const abortListener = () => ffmpeg.terminate();
    signal?.addEventListener('abort', abortListener, { once: true });
    try {
      await ffmpeg.writeFile(inputPath, new Uint8Array(await file.arrayBuffer()), { signal });
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const settings = outputSettings(outputMimeType, quality, source, attempt);
        const exitCode = await ffmpeg.exec([
          '-i', inputPath,
          '-vf', `scale=${settings.width}:${settings.height}`,
          '-sn', '-dn', '-map_metadata', '-1',
          ...settings.args,
          '-y', outputPath,
        ], timeoutMs, { signal });
        throwIfAborted(signal);
        if (exitCode !== 0) throw new MediaProcessingError('processor-failed', `FFmpeg exited with code ${exitCode}`);
        const output = await ffmpeg.readFile(outputPath, 'binary', { signal });
        throwIfAborted(signal);
        if (!(output instanceof Uint8Array) || output.length === 0) throw new MediaProcessingError('output-invalid', 'FFmpeg 未生成视频输出');
        if (output.length <= maxOutputBytes) {
          const outputFile = new File([output], `${file.name.replace(/\.[^.]+$/, '')}-optimized.${outputExtension}`, { type: outputMimeType, lastModified: Date.now() });
          return {
            file: outputFile,
            metadata: {
              width: settings.width,
              height: settings.height,
              durationMs: source.durationMs,
              mimeType: outputMimeType,
              fileName: outputFile.name,
              sizeBytes: outputFile.size,
              container: outputExtension,
              videoCodec: outputMimeType === 'video/mp4' ? 'h264' : 'vp9',
              audioCodec: outputMimeType === 'video/mp4' ? 'aac' : 'opus',
            },
          };
        }
        await ffmpeg.deleteFile(outputPath).catch(() => false);
      }
      throw new MediaProcessingError('output-invalid', '客户端已降低视频分辨率和码率，但输出仍超过 10 MiB');
    } catch (error) {
      if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) throw abortError();
      resetInstance();
      throw error;
    } finally {
      signal?.removeEventListener('abort', abortListener);
      await Promise.allSettled([ffmpeg.deleteFile(inputPath), ffmpeg.deleteFile(outputPath)]);
    }
  });
}
